import type { TenantBranding } from "@/api/types";

export type SuiteProduct = "hire" | "learn" | "manage" | "pulse";

/** The products' names on a tenant's hosts (contract §13a); the root site adds the suite's name ("Hume Learn"). The
 * backend's copy is `core.services.hume.suite.LABELS`. */
const PRODUCTS: Record<SuiteProduct, string> = { hire: "Hire", learn: "Learn", manage: "Manage", pulse: "Pulse" };

/** The sign-in button's text when the organisation hasn't set its own (`TenantBranding.sign_in_label`). */
export const DEFAULT_SIGN_IN_LABEL = "Continue";

/**
 * What the UI calls the suite's products, Hume ID and the sign-in button (contract §13 row 8, §13a). The root site
 * uses the suite's names ("Hume Learn", "Hume ID"); a tenant's hosts are white label and never say "Hume": Hume ID
 * is "your account" there.
 */
export interface ProductLabels {
  /** A tenant's hosts: no "Hume" in any text. */
  whiteLabel: boolean;
  /** This app: "Hume Learn" / "Learn". */
  app: string;
  /** A product of the suite: "Hume Hire" / "Hire". */
  product: (product: string) => string;
  /** The app switcher's heading: "Hume apps" / "Apps". */
  apps: string;
  /** Where people sign in, as a subject or after "to": "Hume ID" / "your account". */
  service: string;
  /** The person's account there, after "your" or "my": "Hume ID" / "account". */
  account: string;
  /** The organisation, by name on its own hosts: "Acme Corp" / "this organisation". */
  org: string;
  /** Where the organisation is managed, after "in": "Hume ID" / "your organisation's account settings". */
  managedIn: string;
  /** The sign-in button: the organisation's own label, else "Continue". */
  signIn: string;
}

export function productLabels(
  branding: Pick<TenantBranding, "is_root"> & { name?: string; sign_in_label?: string },
): ProductLabels {
  const whiteLabel = !branding.is_root;
  const product = (key: string) => {
    const name = PRODUCTS[key as SuiteProduct] ?? key.charAt(0).toUpperCase() + key.slice(1);
    return whiteLabel ? name : `Hume ${name}`;
  };
  return {
    whiteLabel,
    app: product("learn"),
    product,
    apps: whiteLabel ? "Apps" : "Hume apps",
    service: whiteLabel ? "your account" : "Hume ID",
    account: whiteLabel ? "account" : "Hume ID",
    org: (whiteLabel && branding.name?.trim()) || "this organisation",
    managedIn: whiteLabel ? "your organisation's account settings" : "Hume ID",
    signIn: branding.sign_in_label?.trim() || DEFAULT_SIGN_IN_LABEL,
  };
}

/** The root site's labels (the default where no branding is at hand). */
export const ROOT_LABELS = productLabels({ is_root: true });
